import { useCallback, useRef, useState } from 'react';

type PressEvent = React.MouseEvent<HTMLElement> | React.TouchEvent<HTMLElement> | React.PointerEvent<HTMLElement>;

const useLongPress = (onLongPress: any, onClick: any, { shouldPreventDefault = true, delay = 300 } = {}) => {
    const [longPressTriggered, setLongPressTriggered] = useState(false);
    const timeout = useRef<number | null>(null);
    const target = useRef<HTMLElement | null>(null);

    const start = useCallback(
        (event: PressEvent) => {
            if (shouldPreventDefault) {
                const el = event.target as HTMLElement | null;
                if (el) {
                    el.addEventListener('touchend', preventDefault, { passive: false });
                    target.current = el;
                }
            }

            if (timeout) {
                timeout.current = window.setTimeout(() => {
                    onLongPress(event);
                    setLongPressTriggered(true);
                }, delay);
            }
        },
        [onLongPress, delay, shouldPreventDefault]
    );

    const clear = useCallback(
        (event: PressEvent, shouldTriggerClick = true) => {
            timeout.current && clearTimeout(timeout.current);
            shouldTriggerClick && !longPressTriggered && onClick();
            setLongPressTriggered(false);
            if (shouldPreventDefault && target.current) {
                //@ts-ignore
                target.current.removeEventListener('touchend', preventDefault);
            }
        },
        [shouldPreventDefault, onClick, longPressTriggered]
    );

    return {
        onMouseDown: (e: any) => start(e),
        onTouchStart: (e: any) => start(e),
        onMouseUp: (e: any) => clear(e),
        onMouseLeave: (e: any) => clear(e, false),
        onTouchEnd: (e: any) => clear(e)
    };
};

const isTouchEvent = (event: any) => {
    return 'touches' in event;
};

const preventDefault = (event: any) => {
    if (!isTouchEvent(event)) return;

    if (event.touches.length < 2 && event.preventDefault) {
        event.preventDefault();
    }
};

export default useLongPress;
